// src/admin/pages/AboutUsConfig.jsx
import React, { useEffect, useState } from "react";
import { ref, get, set } from "firebase/database";
import { db } from "../../lib/firebase";
import "./AboutUsConfig.css";
import { CONFIG } from "../../config";

/** ---- Config ---- */
const UPLOAD_URL  = CONFIG.UPLOAD_URL;
const IMAGES_BASE = CONFIG.IMAGES_BASE;

/** ---- Helpers ---- */
const imgURL = (name) => (name ? `${IMAGES_BASE}/${encodeURIComponent(name)}` : "");
const asArray = (v, fallbackItem) => {
  if (Array.isArray(v)) return v;
  if (v && typeof v === "object") return Object.values(v);
  return fallbackItem ? [fallbackItem] : [];
};

export default function AboutUsConfig() {
  /** base state */
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState("");

  /** 1) Banner / Slideshow */
  const [useSlideshow, setUseSlideshow]     = useState(false);
  const [heroSlides, setHeroSlides]         = useState([]); // { image, phoneImage, title, subtitle }
  const [heroAutoPlay, setHeroAutoPlay]     = useState(true);
  const [heroIntervalMs, setHeroIntervalMs] = useState(3500);

  // Single banner (legacy style)
  const [desktopImg, setDesktopImg] = useState("");
  const [phoneImg, setPhoneImg]     = useState("");
  const [heroTitle, setHeroTitle] = useState("");
  const [heroSubtitle, setHeroSubtitle] = useState("");
  const [heroButtonText, setHeroButtonText] = useState("");
  const [heroButtonLink, setHeroButtonLink] = useState("");

  /** 2) About Us */
  const [description, setDescription] = useState("");

  /** 2b) About Text Styles */
  const [useSectionTextStyles, setUseSectionTextStyles] = useState(false);
  const [textStyles, setTextStyles] = useState({
    headingColor: "#000000",
    subHeadingColor: "#333333",
    contentColor: "#555555",
    statsColor: "#000000",
    headingSize: "",
    subHeadingSize: "",
    contentSize: "",
    statsSize: "",
  });

  /** 3) Team */
  const [team, setTeam] = useState([{ name: "", description: "", image: "" }]);

  /** 4) Certificates */
  const [certificates, setCertificates] = useState([{ image: "", description: "" }]);

  /** 5) Meta (SEO) — per-page */
  const [metaTitle, setMetaTitle]             = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [metaKeywords, setMetaKeywords]       = useState("");

  /** ---------- Load existing data ---------- */
  useEffect(() => {
    (async () => {
      try {
        const snap = await get(ref(db, "aboutusconfig"));
        const v = (snap.exists() ? snap.val() : {}) || {};

        // banner/slideshow
        setUseSlideshow(!!v.useSlideshow);
        setHeroSlides(asArray(v.heroSlides));
        setHeroAutoPlay(v.heroAutoPlay !== undefined ? !!v.heroAutoPlay : true);
        setHeroIntervalMs(Number.isFinite(v.heroIntervalMs) ? v.heroIntervalMs : 3500);
        setDesktopImg(v.desktopImage || "");
        setPhoneImg(v.phoneImage || "");
        setHeroTitle(v.heroTitle || "");
        setHeroSubtitle(v.heroSubtitle || "");
        setHeroButtonText(v.heroButtonText || "");
        setHeroButtonLink(v.heroButtonLink || "");

        // about
        setDescription(v.description || "");
        setUseSectionTextStyles(v?.useSectionTextStyles === true);
        const ts = v?.textStyles || {};
        setTextStyles({
          headingColor: ts.headingColor || "#000000",
          subHeadingColor: ts.subHeadingColor || "#333333",
          contentColor: ts.contentColor || "#555555",
          statsColor: ts.statsColor || "#000000",
          headingSize: ts.headingSize || "",
          subHeadingSize: ts.subHeadingSize || "",
          contentSize: ts.contentSize || "",
          statsSize: ts.statsSize || "",
        });

        // team
        setTeam(asArray(v.team, { name: "", description: "", image: "" }));

        // certificates
        setCertificates(asArray(v.certificates, { image: "", description: "" }));

        // meta
        setMetaTitle(v?.meta?.metaTitle || "");
        setMetaDescription(v?.meta?.metaDescription || "");
        setMetaKeywords(v?.meta?.metaKeywords || "");
      } catch (e) {
        console.error(e);
        setError("Failed to load configuration.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /** ---------- Upload/Delete (SAME API as CategoryConfig) ---------- */
  const uploadFile = async (setField, file) => {
    const chosen = file ?? null;
    if (!chosen) return;
    try {
      setUploading(true);
      setError("");
      setSuccess("");

      const form = new FormData();
      // field name MUST be "file" for your server (same as CategoryConfig)
      form.append("file", chosen);
      const res = await fetch(UPLOAD_URL, { method: "POST", body: form });

      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const data = await res.json();
      const filename = data?.filename;
      if (!filename) throw new Error("Upload: No filename returned");

      setField(filename);
    } catch (e) {
      console.error(e);
      setError(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = async (name, setField) => {
    if (!name) return setField("");
    if (!window.confirm("Delete this image permanently?")) return;

    try {
      setError("");
      setSuccess("");

      // Same DELETE pattern as CategoryConfig
      const res = await fetch(`${IMAGES_BASE}/${encodeURIComponent(name)}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);

      setField("");
    } catch (e) {
      console.error(e);
      setError(e.message || "Delete failed");
    }
  };

  const updateTextStyle = (field, value) =>
    setTextStyles((prev) => ({ ...prev, [field]: value }));

  /** ---------- Hero Slides ---------- */
  const addHero = () =>
    setHeroSlides((xs) => [...xs, { image: "", phoneImage: "", title: "", subtitle: "" }]);
  const updateHero = (i, field, val) =>
    setHeroSlides((xs) => xs.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)));
  const removeHero = (i) => setHeroSlides((xs) => xs.filter((_, idx) => idx !== i));

  const uploadHeroDesktop = (i, f) => uploadFile((fn) => updateHero(i, "image", fn), f);
  const uploadHeroPhone   = (i, f) => uploadFile((fn) => updateHero(i, "phoneImage", fn), f);

  const deleteHeroDesktop = async (i) => {
    const n = heroSlides?.[i]?.image;
    if (!n) return updateHero(i, "image", "");
    await deleteFile(n, () => updateHero(i, "image", ""));
  };
  const deleteHeroPhone = async (i) => {
    const n = heroSlides?.[i]?.phoneImage;
    if (!n) return updateHero(i, "phoneImage", "");
    await deleteFile(n, () => updateHero(i, "phoneImage", ""));
  };

  /** ---------- Team ---------- */
  const addTeam = () =>
    setTeam((t) => [...t, { name: "", description: "", image: "" }]);

  const updateTeam = (i, field, val) =>
    setTeam((t) => t.map((m, idx) => (idx === i ? { ...m, [field]: val } : m)));

  const removeTeam = (i) =>
    setTeam((t) =>
      t.length > 1 ? t.filter((_, idx) => idx !== i) : [{ name: "", description: "", image: "" }]
    );

  const uploadTeamImage = (i, f) =>
    uploadFile((fn) => updateTeam(i, "image", fn), f);

  const deleteTeamImage = async (i) => {
    const name = team?.[i]?.image;
    if (!name) return updateTeam(i, "image", "");
    await deleteFile(name, () => updateTeam(i, "image", ""));
  };

  /** ---------- Certificates ---------- */
  const addCert = () =>
    setCertificates((list) => [...list, { image: "", description: "" }]);

  const updateCert = (i, field, val) =>
    setCertificates((list) =>
      list.map((c, idx) => (idx === i ? { ...c, [field]: val } : c))
    );

  const removeCert = (i) =>
    setCertificates((list) =>
      list.length > 1 ? list.filter((_, idx) => idx !== i) : [{ image: "", description: "" }]
    );

  const uploadCertImage = (i, f) =>
    uploadFile((fn) => updateCert(i, "image", fn), f);

  const deleteCertImage = async (i) => {
    const name = certificates?.[i]?.image;
    if (!name) return updateCert(i, "image", "");
    await deleteFile(name, () => updateCert(i, "image", ""));
  };

  /** ---------- Save ---------- */
  const normalize = () => {
    const heroSafe  = asArray(heroSlides);
    const teamSafe  = asArray(team);
    const certsSafe = asArray(certificates);

    return {
      // 1) banner / slideshow
      useSlideshow: !!useSlideshow,
      heroSlides: heroSafe.map((s) => ({
        image: s?.image || "",
        phoneImage: s?.phoneImage || "",
        title: s?.title || "",
        subtitle: s?.subtitle || "",
      })),
      heroAutoPlay: !!heroAutoPlay,
      heroIntervalMs: Number(heroIntervalMs) || 3500,

      // single banner (used when useSlideshow=false)
      desktopImage: desktopImg || "",
      phoneImage:   phoneImg || "",
      heroTitle: heroTitle || "",
      heroSubtitle: heroSubtitle || "",
      heroButtonText: heroButtonText || "",
      heroButtonLink: heroButtonLink || "",

      // 2) about us
      description: description || "",
      useSectionTextStyles: !!useSectionTextStyles,
      textStyles: {
        headingColor: textStyles.headingColor || "",
        subHeadingColor: textStyles.subHeadingColor || "",
        contentColor: textStyles.contentColor || "",
        statsColor: textStyles.statsColor || "",
        headingSize: textStyles.headingSize || "",
        subHeadingSize: textStyles.subHeadingSize || "",
        contentSize: textStyles.contentSize || "",
        statsSize: textStyles.statsSize || "",
      },

      // 3) team
      team: teamSafe.map((t) => ({
        name: t?.name || "",
        description: t?.description || "",
        image: t?.image || "",
      })),

      // 4) certificates
      certificates: certsSafe.map((c) => ({
        image: c?.image || "",
        description: c?.description || "",
      })),

      // 5) meta (SEO)
      meta: {
        metaTitle:       metaTitle || "",
        metaDescription: metaDescription || "",
        metaKeywords:    metaKeywords || "",
      },
    };
  };

  const save = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = normalize();
      await set(ref(db, "aboutusconfig"), payload);
      setSuccess("Saved successfully ✔");
    } catch (e) {
      console.error(e);
      const msg = e?.message?.includes("Permission denied")
        ? "Permission denied. Please sign in as adityasp193@gmail.com and check Firebase rules."
        : e?.message || "Save failed";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  /** ---------- Safe arrays for rendering ---------- */
  const heroSlidesSafe   = asArray(heroSlides);
  const teamSafe         = asArray(team, { name: "", description: "", image: "" });
  const certificatesSafe = asArray(certificates, { image: "", description: "" });

  if (loading) return <div className="aucfg-loading">Loading…</div>;

  return (
    <section className="aucfg-wrap">
      <h2 className="aucfg-title">About Us Configuration</h2>
      {success && <p style={{ color: "green" }}>{success}</p>}
      {error && <p className="aucfg-error">{error}</p>}
      {uploading && <p className="aucfg-note">Uploading image…</p>}

      {/* 1) BANNER / SLIDESHOW */}
      <div className="aucfg-card">
        <div className="aucfg-headrow">
          <h3 className="aucfg-subtitle">Banner / Slideshow</h3>
          <label className="switch">
            <input
              type="checkbox"
              checked={useSlideshow}
              onChange={(e) => setUseSlideshow(e.target.checked)}
            />
            <span>Use slideshow (instead of single banner)</span>
          </label>
        </div>

        {!useSlideshow ? (
          <div className="aucfg-row">
            <div className="aucfg-col">
              <label>Desktop Banner</label>
              {desktopImg ? (
                <img src={imgURL(desktopImg)} alt="" className="aucfg-preview" />
              ) : (
                <div className="aucfg-placeholder">No image</div>
              )}
              <div className="aucfg-actions-inline">
                <label className="aucfg-btn">
                  Upload
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploading}
                    onChange={(e) => uploadFile(setDesktopImg, e.target.files?.[0])}
                    style={{ display: "none" }}
                  />
                </label>
                <button
                  className="aucfg-btn danger"
                  onClick={() => deleteFile(desktopImg, setDesktopImg)}
                  disabled={!desktopImg}
                >
                  Delete
                </button>
              </div>
            </div>

            <div className="aucfg-col">
              <label>Phone Banner</label>
              {phoneImg ? (
                <img src={imgURL(phoneImg)} alt="" className="aucfg-preview" />
              ) : (
                <div className="aucfg-placeholder">No image</div>
              )}
              <div className="aucfg-actions-inline">
                <label className="aucfg-btn">
                  Upload
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploading}
                    onChange={(e) => uploadFile(setPhoneImg, e.target.files?.[0])}
                    style={{ display: "none" }}
                  />
                </label>
                <button
                  className="aucfg-btn danger"
                  onClick={() => deleteFile(phoneImg, setPhoneImg)}
                  disabled={!phoneImg}
                >
                  Delete
                </button>
              </div>
            </div>

            <div className="aucfg-col">
              <label>Banner Title</label>
              <input
                value={heroTitle}
                onChange={(e) => setHeroTitle(e.target.value)}
                placeholder="Banner title"
              />
              <label>Banner Subtitle</label>
              <input
                value={heroSubtitle}
                onChange={(e) => setHeroSubtitle(e.target.value)}
                placeholder="Banner subtitle"
              />
              <label>Button Text (optional)</label>
              <input
                value={heroButtonText}
                onChange={(e) => setHeroButtonText(e.target.value)}
                placeholder="e.g., Contact Us"
              />
              <label>Button Link (optional)</label>
              <input
                value={heroButtonLink}
                onChange={(e) => setHeroButtonLink(e.target.value)}
                placeholder="/contact"
              />
            </div>
          </div>
        ) : (
          <>
            <div className="aucfg-row tight">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={heroAutoPlay}
                  onChange={(e) => setHeroAutoPlay(e.target.checked)}
                />
                <span>Auto-play</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                Interval (ms)
                <input
                  type="number"
                  value={heroIntervalMs}
                  onChange={(e) => setHeroIntervalMs(e.target.value)}
                  min={1000}
                  step={100}
                  style={{ width: 120 }}
                />
              </label>
              <button className="aucfg-btn ghost" onClick={addHero}>
                + Add Slide
              </button>
            </div>

            {heroSlidesSafe.map((s, i) => (
              <div key={i} className="aucfg-hero-row">
                <div className="aucfg-col">
                  <label>Desktop Image</label>
                  {s.image ? (
                    <img src={imgURL(s.image)} alt="" className="aucfg-preview" />
                  ) : (
                    <div className="aucfg-placeholder">No image</div>
                  )}
                  <div className="aucfg-actions-inline">
                    <label className="aucfg-btn">
                      Upload
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploading}
                        onChange={(e) => uploadHeroDesktop(i, e.target.files?.[0])}
                        style={{ display: "none" }}
                      />
                    </label>
                    <button
                      className="aucfg-btn danger"
                      onClick={() => deleteHeroDesktop(i)}
                      disabled={!s.image}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                <div className="aucfg-col">
                  <label>Phone Image</label>
                  {s.phoneImage ? (
                    <img src={imgURL(s.phoneImage)} alt="" className="aucfg-preview" />
                  ) : (
                    <div className="aucfg-placeholder">No image</div>
                  )}
                  <div className="aucfg-actions-inline">
                    <label className="aucfg-btn">
                      Upload
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploading}
                        onChange={(e) => uploadHeroPhone(i, e.target.files?.[0])}
                        style={{ display: "none" }}
                      />
                    </label>
                    <button
                      className="aucfg-btn danger"
                      onClick={() => deleteHeroPhone(i)}
                      disabled={!s.phoneImage}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                <div className="aucfg-col">
                  <label>Title</label>
                  <input
                    value={s.title || ""}
                    onChange={(e) => updateHero(i, "title", e.target.value)}
                    placeholder="Slide title"
                  />
                  <label>Subtitle</label>
                  <input
                    value={s.subtitle || ""}
                    onChange={(e) => updateHero(i, "subtitle", e.target.value)}
                    placeholder="Optional subtitle"
                  />
                </div>

                <button
                  className="aucfg-btn danger outline"
                  onClick={() => removeHero(i)}
                >
                  Remove Slide
                </button>
              </div>
            ))}
          </>
        )}
      </div>

      {/* 2) ABOUT US */}
      <div className="aucfg-card">
        <h3 className="aucfg-subtitle">About Us</h3>
        <textarea
          rows={5}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Write about your company..."
        />
      </div>

      {/* 2b) TEXT STYLES */}
      <div className="aucfg-card">
        <div className="aucfg-headrow">
          <h3 className="aucfg-subtitle">Text Styles</h3>
          <label className="switch">
            <input
              type="checkbox"
              checked={useSectionTextStyles}
              onChange={(e) => setUseSectionTextStyles(e.target.checked)}
            />
            <span>Use section text styles</span>
          </label>
        </div>

        <div className="aucfg-row">
          <div className="aucfg-col">
            <label>Heading Color</label>
            <input
              type="color"
              value={textStyles.headingColor || "#000000"}
              onChange={(e) => updateTextStyle("headingColor", e.target.value)}
              style={{ width: "90%", height: 40 }}
            />
          </div>
          <div className="aucfg-col">
            <label>Heading Size</label>
            <input
              type="text"
              value={textStyles.headingSize || ""}
              onChange={(e) => updateTextStyle("headingSize", e.target.value)}
              placeholder="e.g., 2.5rem"
            />
          </div>
        </div>

        <div className="aucfg-row">
          <div className="aucfg-col">
            <label>Sub Heading Color</label>
            <input
              type="color"
              value={textStyles.subHeadingColor || "#333333"}
              onChange={(e) => updateTextStyle("subHeadingColor", e.target.value)}
              style={{ width: "90%", height: 40 }}
            />
          </div>
          <div className="aucfg-col">
            <label>Sub Heading Size</label>
            <input
              type="text"
              value={textStyles.subHeadingSize || ""}
              onChange={(e) => updateTextStyle("subHeadingSize", e.target.value)}
              placeholder="e.g., 1.5rem"
            />
          </div>
        </div>

        <div className="aucfg-row">
          <div className="aucfg-col">
            <label>Content Color</label>
            <input
              type="color"
              value={textStyles.contentColor || "#555555"}
              onChange={(e) => updateTextStyle("contentColor", e.target.value)}
              style={{ width: "90%", height: 40 }}
            />
          </div>
          <div className="aucfg-col">
            <label>Content Size</label>
            <input
              type="text"
              value={textStyles.contentSize || ""}
              onChange={(e) => updateTextStyle("contentSize", e.target.value)}
              placeholder="e.g., 1rem"
            />
          </div>
        </div>

        <div className="aucfg-row">
          <div className="aucfg-col">
            <label>Stats Color</label>
            <input
              type="color"
              value={textStyles.statsColor || "#000000"}
              onChange={(e) => updateTextStyle("statsColor", e.target.value)}
              style={{ width: "90%", height: 40 }}
            />
          </div>
          <div className="aucfg-col">
            <label>Stats Size</label>
            <input
              type="text"
              value={textStyles.statsSize || ""}
              onChange={(e) => updateTextStyle("statsSize", e.target.value)}
              placeholder="e.g., 2rem"
            />
          </div>
        </div>
      </div>

      {/* 3) TEAM */}
      <div className="aucfg-card">
        <div className="aucfg-headrow">
          <h3 className="aucfg-subtitle">Team</h3>
          <button className="aucfg-btn ghost" onClick={addTeam}>
            + Add Member
          </button>
        </div>

        {teamSafe.map((m, i) => (
          <div key={i} className="aucfg-teamrow">
            <div className="aucfg-col">
              <label>Name</label>
              <input
                value={m.name}
                onChange={(e) => updateTeam(i, "name", e.target.value)}
                placeholder="Team member name"
              />
            </div>
            <div className="aucfg-col">
              <label>Description</label>
              <textarea
                rows={3}
                value={m.description}
                onChange={(e) => updateTeam(i, "description", e.target.value)}
                placeholder="Short bio"
              />
            </div>
            <div className="aucfg-col">
              <label>Image</label>
              {m.image ? (
                <img src={imgURL(m.image)} alt="" className="aucfg-preview" />
              ) : (
                <div className="aucfg-placeholder">No image</div>
              )}
              <div className="aucfg-actions-inline">
                <label className="aucfg-btn">
                  Upload
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploading}
                    onChange={(e) => uploadTeamImage(i, e.target.files?.[0])}
                    style={{ display: "none" }}
                  />
                </label>
                <button
                  className="aucfg-btn danger"
                  onClick={() => deleteTeamImage(i)}
                  disabled={!m.image}
                >
                  Delete
                </button>
              </div>
            </div>
            <button
              className="aucfg-btn danger outline"
              onClick={() => removeTeam(i)}
            >
              Remove Member
            </button>
          </div>
        ))}
      </div>

      {/* 4) CERTIFICATES */}
      <div className="aucfg-card">
        <div className="aucfg-headrow">
          <h3 className="aucfg-subtitle">Certificates</h3>
          <button className="aucfg-btn ghost" onClick={addCert}>
            + Add Certificate
          </button>
        </div>

        {certificatesSafe.map((c, i) => (
          <div key={i} className="aucfg-certrow">
            <div className="aucfg-col">
              <label>Image</label>
              {c.image ? (
                <img src={imgURL(c.image)} alt="" className="aucfg-preview" />
              ) : (
                <div className="aucfg-placeholder">No image</div>
              )}
              <div className="aucfg-actions-inline">
                <label className="aucfg-btn">
                  Upload
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploading}
                    onChange={(e) => uploadCertImage(i, e.target.files?.[0])}
                    style={{ display: "none" }}
                  />
                </label>
                <button
                  className="aucfg-btn danger"
                  onClick={() => deleteCertImage(i)}
                  disabled={!c.image}
                >
                  Delete
                </button>
              </div>
            </div>
            <div className="aucfg-col">
              <label>Description</label>
              <textarea
                rows={3}
                value={c.description}
                onChange={(e) => updateCert(i, "description", e.target.value)}
                placeholder="Certificate details"
              />
            </div>
            <button
              className="aucfg-btn danger outline"
              onClick={() => removeCert(i)}
            >
              Remove Certificate
            </button>
          </div>
        ))}
      </div>

      {/* 5) META (SEO) */}
      <div className="aucfg-card">
        <h3 className="aucfg-subtitle">Page Meta Data (SEO)</h3>
        <div className="aucfg-row column">
          <div className="aucfg-col">
            <label>Meta Title</label>
            <input
              type="text"
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              placeholder="About Regency Interiors | Our Story & Expertise"
            />
          </div>
          <div className="aucfg-col">
            <label>Meta Description</label>
            <textarea
              rows={3}
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              placeholder="Short SEO description (under 160 characters)"
            />
          </div>
          <div className="aucfg-col">
            <label>Meta Keywords</label>
            <input
              type="text"
              value={metaKeywords}
              onChange={(e) => setMetaKeywords(e.target.value)}
              placeholder="about us, interior design, regency interiors"
            />
          </div>
        </div>
      </div>

      <div className="aucfg-actions">
        <button
          className="aucfg-btn primary"
          onClick={save}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save aboutusconfig"}
        </button>
      </div>
    </section>
  );
}
